import { FC, useCallback, useEffect, useMemo, useRef, useState, MouseEvent } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";

import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Typography,
} from "@mui/material";
import { GridView, InfoOutlined } from "@mui/icons-material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { useAsu, RangeType } from "@framework/provider-asu";
import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";
import { useSettings } from "@framework/provider-settings";
import { useApiCall } from "@framework/react-hooks";

import TrainImage from "../../../../../../assets/images/train.png";
import { InformationTable } from "../../../../../../components/common/tables/information-table";
import { DashboardAccordion } from "../../ui/accordion";
import {
  IPictureResponse,
  ITrainControlsValues,
  ITrainControlsResponse,
  ISchemeObjectInfoResponse,
} from "./interfaces";
import { StyledImage, StyledWrapper } from "./styled";
import { StyledButton } from "../../../../../../components/common/styled/button";

import { TiAnalogDisplay } from "./components/ti-analog-display";
import { TiLabel } from "./components/ti-label";
import { TiLedRound } from "./components/ti-led-round";
import { TiSevenSegmentAnalog } from "./components/ti-seven-segment-analog";
import { TiThermometer } from "./components/ti-thermometer";
import { TiAngularGauge } from "./components/ti-angular-gauge";
import { TiLinearGauge } from "./components/ti-linear-gauge";
import { TiSchemeButton } from "./components/ti-scheme-button";
import { TiSchemeLine } from "./components/ti-scheme-line";
import { TiSchemeImage } from "./components/ti-scheme-image";
import { OpenableWindow } from "../../ui/openable-window";
import { TrainDefectsDialog } from "../defects/train-defects-dialog";
import { normalizeDefectRow } from "../defects/filter-defects-by-binding";
import { IDefectResponse } from "../defects/interfaces";
import { openPopupWithParams } from "../../views/station-scheme/components/image-view/components/SchemeArea/utils";
import { mergeRecorderModeControls } from "./recorder/mergeSeries";
import { formatSchemeControlMeasurementValue } from "../../utils/measurement-format";
import { useSubscribe } from "../../../../../subscribe/provider";
import { ISubscribeScalarMessage, SubscribeCode, SubscribeMessage } from "../../../../../subscribe/types";
import { ANALYTICS_MEASURE_TYPE, ANALYTICS_OBJECT } from "../analytics/interfaces";
import { getTrainParameterOptionId } from "./analytics-binding";
import { SchemeCanvas } from "./scheme-canvas";

function coerceSchemeBindId(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export enum TrainSchemeView {
  infoTable = "infoTable",
  controls = "controls",
}

interface ITrainProps {
  isNarrow?: boolean;
}

export const Train: FC<ITrainProps> = ({ isNarrow }) => {
  const {
    selectedItem,
    startTimestamp,
    endTimestamp,
    recorderWindowStart,
    userSelectedRangeType,
    recorderModeInterval,
    userSelectedRecorderModePeriod,
    selectStation,
    selectTrain,
    dbTz,
    setPendingAnalyticsParams,
  } = useAsu();

  const user = useUser<IUser>();
  const { connected: subscribeConnected, enabled: subscribeModeEnabled, subscribe } = useSubscribe();
  const subscribeModeActive = subscribeModeEnabled && subscribeConnected;

  const settings = useSettings();

  const imageRef = useRef<HTMLImageElement | null>(null);
  const pictureAbortControllerRef = useRef<AbortController | null>(null);
  const controlsAbortControllerRef = useRef<AbortController | null>(null);
  const schemeObjectInfoAbortControllerRef = useRef<AbortController | null>(null);
  const clickTimerRef = useRef<number | null>(null);

  const [trainImage, setTrainImage] = useState<string>(TrainImage);
  const [imageNotFound, setImageNotFound] = useState<boolean>(false);
  const [imageExpanded, setImageExpanded] = useState<boolean>(false);
  const [trainControls, setTrainControls] = useState<ITrainControlsResponse[]>([]);
  const [schemeObjectDialogOpen, setSchemeObjectDialogOpen] = useState<boolean>(false);
  const [schemeObjectInfo, setSchemeObjectInfo] = useState<ISchemeObjectInfoResponse | null>(null);
  const [view, setView] = useState<TrainSchemeView>(TrainSchemeView.controls);
  const [imageWidth, setImageWidth] = useState<number | undefined>(undefined);
  const [imageHeight, setImageHeight] = useState<number | undefined>(undefined);
  const [imageTrainId, setImageTrainId] = useState<number | null>(null);

  const [defectsDialogOpen, setDefectsDialogOpen] = useState<boolean>(false);
  const [defectsMaxRate, setDefectsMaxRate] = useState<number | null>(null);
  const [bindingDefectsMaxRates, setBindingDefectsMaxRates] = useState<Record<string, number>>({});
  const [defectsDialogBinding, setDefectsDialogBinding] = useState<{
    bindObjectType: number;
    idBindObject: number | null;
  } | null>(null);

  const [searchParams] = useSearchParams();
  const { formatMessage } = useIntl();

  const flashUnacknowledgedAlarms = user.profile.interfaceSettings.ts_scheme.signalUnacknowledgedEvents;
  const flashFaultAlarms = user.profile.interfaceSettings.ts_scheme.additionalFaultAlarm;

  const schemeInNewWindow = user.profile.interfaceSettings.openTrainSchemeInNewWindow;

  const trainId = (selectedItem && "TrainId" in selectedItem && selectedItem.TrainId) || null;
  const isRecorderMode = userSelectedRangeType === RangeType.RECORDER_MODE;
  const prevTrainIdForControls = useRef(trainId);
  const subscribeBootstrapTrainIdRef = useRef<number | null>(null);
  const prevTrainIdForDefects = useRef(trainId);
  const schemeState = settings.getSchemeState();
  const { train } = schemeState;
  const onlyPage = searchParams.get("onlyPage") === "true";
  const currentComponent = searchParams.get("component");
  const title = formatMessage({ id: "pages.scheme.trainScheme.title" });
  const showControls = view === TrainSchemeView.controls;
  const fitScheme =
    (user.profile.interfaceSettings.scaleTrainScheme ?? false) &&
    (showControls || imageExpanded) &&
    trainImage !== TrainImage &&
    !imageNotFound;

  const shouldShowComponent = useMemo(() => {
    if (!onlyPage) return true;
    if (currentComponent) return currentComponent === "train";
    return !!user.profile.interfaceSettings.showTrainScheme;
  }, [onlyPage, currentComponent, user.profile.interfaceSettings.showTrainScheme]);

  const formattedTrainControls = useMemo(
    () =>
      trainControls.map(control => {
        const formattedValue = formatSchemeControlMeasurementValue({
          bindObjectType: control.bindObjectType,
          componentName: control.componentName,
          rawValue: control.rawValue,
          settings: user.profile.interfaceSettings.dataFormat,
          sourceValue: control.value,
          valueUnits: control.valueUnits,
        });

        if (formattedValue === control.value) {
          return control;
        }

        return {
          ...control,
          value: formattedValue,
        };
      }),
    [trainControls, user.profile.interfaceSettings.dataFormat],
  );

  const subscribedMeasureIdsKey = useMemo(
    () =>
      Array.from(
        new Set(
          trainControls
            .filter(control => control.bindObjectType === 4)
            .map(control => coerceSchemeBindId(control.idBindObject))
            .filter((id): id is number => id !== null),
        ),
      )
        .sort((a, b) => a - b)
        .join(","),
    [trainControls],
  );

  const subscribedStatusRequestsKey = useMemo(() => {
    const requests = trainControls
      .map(control => {
        const id = coerceSchemeBindId(control.idBindObject);
        if (id === null) return null;
        if (control.bindObjectType === 1) return `${SubscribeCode.STATUS_TOPLEVEL}:${id}`;
        if (control.bindObjectType === 2) return `${SubscribeCode.STATUS_TRAIN}:${id}`;
        if (control.bindObjectType === 3) return `${SubscribeCode.STATUS_POINT}:${id}`;
        return null;
      })
      .filter((value): value is string => value !== null);

    if (trainId) {
      requests.push(`${SubscribeCode.STATUS_TRAIN}:${trainId}`, `${SubscribeCode.PARAM}:${trainId}`);
    }
    return Array.from(new Set(requests)).sort().join(",");
  }, [trainControls, trainId]);

  const ensureAnalyticsExpanded = useCallback(() => {
    const schemeState = settings.getSchemeState();

    if (!schemeState.analytics) {
      settings.setSchemeState({
        ...schemeState,
        analytics: true,
      });
    }
  }, [settings]);

  const { fn: getPicture, isLoading: isPictureLoading } = useApiCall<void>(
    async (api, { trainId }: { trainId: number }, signal: AbortSignal) => {
      try {
        const json = (await api.fetchJson({
          url: `/trains/${trainId}/picture`,
          method: "GET",
          signal,
        })) as IPictureResponse | null;
        if (json && json.PictureData) {
          setTrainImage(`data:image/png;base64,${json.PictureData}`);
          setImageTrainId(trainId);
        } else {
          setTrainImage(TrainImage);
        }
      } catch (e) {
        if ((e as Error).name !== "AbortError") {
          setImageNotFound(true);
          console.error(e);
        }
      }
    },
    { success: false, error: false },
  );

  const { fn: getControls, isLoading: isControlsLoading } = useApiCall<ITrainControlsResponse[]>(
    async (api, { trainId, startTimestamp, endTimestamp }: ITrainControlsValues, signal: AbortSignal) => {
      try {
        const json = (await api.fetchJson({
          url: `/trains/${trainId}/scheme`,
          method: "GET",
          data: {
            startTimestamp,
            endTimestamp,
          },
          signal,
        })) as ITrainControlsResponse[] | null;
        return json ?? [];
      } catch (e) {
        if ((e as Error).name !== "AbortError") {
          setTrainControls([]);
          console.error(e);
        }
        return [];
      }
    },
    { success: false, error: false },
  );

  const { fn: getSchemeObjectInfo } = useApiCall<ISchemeObjectInfoResponse | null>(
    async (api, { idSchemeObject }: { idSchemeObject: number }, signal: AbortSignal) => {
      try {
        return (await api.fetchJson({
          url: `/trains/scheme-object-info/${idSchemeObject}`,
          method: "GET",
          signal,
        })) as ISchemeObjectInfoResponse | null;
      } catch (e) {
        if ((e as Error).name !== "AbortError") {
          console.error(e);
        }
        return null;
      }
    },
    { success: false, error: false },
  );

  const { fn: getTrainDefects } = useApiCall<IDefectResponse[]>(
    async (
      api,
      { trainId, startTimestamp, endTimestamp }: { trainId: number; startTimestamp: string; endTimestamp: string },
    ) => {
      const json = (await api.fetchJson({
        url: `/trains/${trainId}/defects`,
        method: "GET",
        data: {
          startTimestamp,
          endTimestamp,
        },
      })) as IDefectResponse[] | null;
      return json ?? [];
    },
    { success: false },
  );

  const fetchPicture = () => {
    if (trainId) {
      if (pictureAbortControllerRef.current) {
        pictureAbortControllerRef.current.abort();
      }

      const controller = new AbortController();
      pictureAbortControllerRef.current = controller;

      void getPicture(undefined, { trainId }, controller.signal);
    }
  };

  const fetchControls = () => {
    if (!isRecorderMode && controlsAbortControllerRef.current) {
      controlsAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    controlsAbortControllerRef.current = controller;

    void getControls(
      undefined,
      {
        trainId,
        startTimestamp,
        endTimestamp,
      },
      controller.signal,
    ).then(json => {
      const newControlsArray = Array.isArray(json) ? json : [];
      if (isRecorderMode) {
        setTrainControls(prev => mergeRecorderModeControls(prev, newControlsArray, recorderWindowStart, dbTz));
      } else {
        setTrainControls(newControlsArray);
      }
    });
  };

  const fetchSchemeObjectInfo = async (idSchemeObject: number): Promise<ISchemeObjectInfoResponse | null> => {
    if (schemeObjectInfoAbortControllerRef.current) {
      schemeObjectInfoAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    schemeObjectInfoAbortControllerRef.current = controller;

    const result = await getSchemeObjectInfo(undefined, { idSchemeObject }, controller.signal);
    return result ?? null;
  };

  const handleSingleClickInfo = async (idSchemeObject: number) => {
    const json = await fetchSchemeObjectInfo(idSchemeObject);

    if (json) {
      setSchemeObjectInfo(json);
      setSchemeObjectDialogOpen(true);
    } else {
      setSchemeObjectInfo(null);
      setSchemeObjectDialogOpen(false);
    }
  };

  const handleSingleClick = (
    e: MouseEvent<HTMLDivElement>,
    ctrl: ITrainControlsResponse,
    resolvedBindId: number | null,
    jumpBindType: number | null,
    hasBindObject: boolean,
    isDefectsControl: boolean,
  ) => {
    if (!hasBindObject) {
      return;
    }

    if (ctrl.componentName === "TiSchemeButton" || ctrl.componentName === "TiSchemeImage") {
      if (resolvedBindId != null && (jumpBindType === 1 || jumpBindType === 2)) {
        if (jumpBindType === 1) {
          selectStation(resolvedBindId);
          return;
        }

        if (schemeInNewWindow) {
          openPopupWithParams(e, resolvedBindId);
        } else {
          selectTrain(resolvedBindId);
        }

        return;
      }
    }

    if (ctrl.componentName === "TiSchemeImage") {
      return;
    }

    if (isDefectsControl && trainId) {
      setDefectsDialogBinding({
        bindObjectType: jumpBindType ?? 2,
        idBindObject: resolvedBindId,
      });
      setDefectsDialogOpen(true);
    } else {
      void handleSingleClickInfo(ctrl.idSchemeObject);
    }
  };

  const handleDoubleClick = async (ctrl: ITrainControlsResponse) => {
    if (!user.profile.interfaceSettings.showMeasureData) {
      return;
    }

    const json = await fetchSchemeObjectInfo(ctrl.idSchemeObject);

    if (!json) {
      return;
    }

    const isMeasurement = !!json.mName;
    const isPoint = !isMeasurement && !!json.pName;

    if (isMeasurement) {
      ensureAnalyticsExpanded();
      setPendingAnalyticsParams({
        measureType: ANALYTICS_MEASURE_TYPE.TREND,
        object: ANALYTICS_OBJECT.POINT,
        objectId: json.pId ?? undefined,
        objectTitle: `${json.pName ?? ""}${
          json.pDirection ? ` ${formatMessage({ id: `enums.pointDirection.${json.pDirection}` })}` : ""
        }`,
        parameterTitle: json.mName!,
      });
      return;
    }

    if (isPoint) {
      ensureAnalyticsExpanded();
      setPendingAnalyticsParams({
        measureType: ANALYTICS_MEASURE_TYPE.TREND,
        object: ANALYTICS_OBJECT.POINT,
        objectId: json.pId ?? undefined,
        objectTitle: `${json.pName ?? ""}${
          json.pDirection ? ` ${formatMessage({ id: `enums.pointDirection.${json.pDirection}` })}` : ""
        }`,
        selectAllParameters: true,
        parameterTitle: "",
      });
      return;
    }

    const trainParameterOptionId = getTrainParameterOptionId(json.bindingParam);

    if (ctrl.bindObjectType === 2 && json.tName && trainParameterOptionId) {
      ensureAnalyticsExpanded();
      setPendingAnalyticsParams({
        measureType: ANALYTICS_MEASURE_TYPE.TREND,
        object: ANALYTICS_OBJECT.TRAIN,
        objectId: ctrl.idBindObject ?? undefined,
        objectTitle: json.tName,
        parameterTitle: trainParameterOptionId,
      });
    }
  };

  const handleControlClick = (
    e: MouseEvent<HTMLDivElement>,
    ctrl: ITrainControlsResponse,
    resolvedBindId: number | null,
    jumpBindType: number | null,
    hasBindObject: boolean,
    isDefectsControl: boolean,
  ) => {
    if (clickTimerRef.current !== null) {
      window.clearTimeout(clickTimerRef.current);
    }

    clickTimerRef.current = window.setTimeout(() => {
      clickTimerRef.current = null;

      handleSingleClick(e, ctrl, resolvedBindId, jumpBindType, hasBindObject, isDefectsControl);
    }, 250);
  };

  const handleControlDoubleClick = (ctrl: ITrainControlsResponse) => {
    if (clickTimerRef.current !== null) {
      window.clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }

    void handleDoubleClick(ctrl);
  };

  const setExpanded = useCallback(
    (isExpanded: boolean): void => {
      const newSchemeState = {
        ...schemeState,
        train: isExpanded,
      };

      settings.setSchemeState(newSchemeState);
    },
    [schemeState, settings],
  );

  const toggleImageExpanded = useCallback(() => {
    if (imageNotFound) {
      return;
    }

    setImageExpanded(prev => !prev);
  }, [imageNotFound]);

  const handleToggleView = useCallback((_event: React.MouseEvent<HTMLElement>, newView: TrainSchemeView | null) => {
    if (newView !== null) {
      setView(newView);
    }
  }, []);

  const handleImageLoad = useCallback(() => {
    if (imageRef.current && trainImage !== TrainImage && imageTrainId === trainId && !imageNotFound) {
      const width = imageRef.current.naturalWidth;
      setImageWidth(width);
      setImageHeight(imageRef.current.naturalHeight);
    }
  }, [trainImage, trainId, imageTrainId, imageNotFound]);

  const handleDialogClose = useCallback(() => {
    setSchemeObjectDialogOpen(false);
  }, []);

  const handleDefectsDialogClose = useCallback(() => {
    setDefectsDialogOpen(false);
  }, []);

  const isBindingDefectsControl = (ctrl: ITrainControlsResponse) => {
    if (ctrl.componentName !== "TiLedRound") return false;
    return true;
  };

  useEffect(() => {
    if (trainId) {
      setTrainImage(TrainImage);
      setImageNotFound(false);
      setImageExpanded(false);
      setImageWidth(undefined);
      setImageHeight(undefined);
      setView(TrainSchemeView.controls);
      fetchPicture();
    }
  }, [trainId]);

  useEffect(() => {
    setTrainControls([]);
    subscribeBootstrapTrainIdRef.current = null;
  }, [trainId, isRecorderMode, recorderModeInterval, userSelectedRecorderModePeriod]);

  useEffect(() => {
    if (!trainId) return;

    if (subscribeModeActive && subscribeBootstrapTrainIdRef.current === trainId) {
      return;
    }

    if (isRecorderMode) {
      if (!recorderWindowStart) return;
      if (prevTrainIdForControls.current !== null && trainId !== prevTrainIdForControls.current) {
        prevTrainIdForControls.current = trainId;
        return;
      }
    }

    if (subscribeModeActive) {
      subscribeBootstrapTrainIdRef.current = trainId;
    }
    fetchControls();
  }, [trainId, startTimestamp, endTimestamp, isRecorderMode, recorderWindowStart, subscribeModeActive]);

  useEffect(() => {
    if (!subscribeModeActive || !subscribedMeasureIdsKey) {
      return;
    }

    const handleMessages = (messages: SubscribeMessage[]): void => {
      if (messages.length === 0) {
        return;
      }
      const latestByMeasure = new Map<number, ISubscribeScalarMessage>();
      for (const message of messages) {
        if (!("idMeasure" in message)) {
          continue;
        }
        const previous = latestByMeasure.get(message.idMeasure);
        if (!previous || new Date(message.Date).getTime() >= new Date(previous.Date).getTime()) {
          latestByMeasure.set(message.idMeasure, message);
        }
      }

      setTrainControls(previous =>
        previous.map(control => {
          const idMeasure = coerceSchemeBindId(control.idBindObject);
          const message = idMeasure === null ? undefined : latestByMeasure.get(idMeasure);
          if (!message) {
            return control;
          }
          return {
            ...control,
            value: message.Value1 === null ? null : String(message.Value1),
            rawValue: message.Value1,
            ldd: message.Date,
            alarmStatus: message.AlarmStatus,
          };
        }),
      );
    };

    const cleanups = subscribedMeasureIdsKey
      .split(",")
      .flatMap(idMeasure => [subscribe({ id: Number(idMeasure), type: SubscribeCode.SCALAR }, handleMessages)]);

    return () => cleanups.forEach(cleanup => cleanup());
  }, [subscribe, subscribeModeActive, subscribedMeasureIdsKey]);

  useEffect(() => {
    if (!subscribeModeActive || !subscribedStatusRequestsKey) {
      return;
    }

    const handleMessages = (messages: SubscribeMessage[]): void => {
      if (messages.length === 0) {
        return;
      }

      setTrainControls(previous =>
        previous.map(control => {
          const bindId = coerceSchemeBindId(control.idBindObject);
          const relevant = messages.filter(message => {
            if ("ObjectId" in message) {
              return bindId === message.ObjectId;
            }
            return "idTrain" in message && control.bindObjectType === 2 && bindId === message.idTrain;
          });
          const message = relevant[relevant.length - 1];
          if (!message) {
            return control;
          }
          if ("ObjectId" in message) {
            return { ...control, alarmStatus: message.AlarmStatus, ldd: message.Date };
          }
          if ("Value" in message && message.Value !== undefined) {
            return {
              ...control,
              value: String(message.Value),
              rawValue: message.Value,
              ldd: message.Date,
            };
          }
          return { ...control, ldd: message.Date };
        }),
      );
    };

    const cleanups = subscribedStatusRequestsKey.split(",").map(value => {
      const [type, id] = value.split(":").map(Number);
      return subscribe({ type: type as SubscribeCode, id }, handleMessages);
    });
    return () => cleanups.forEach(cleanup => cleanup());
  }, [subscribe, subscribeModeActive, subscribedStatusRequestsKey]);

  useEffect(() => {
    if (!trainId || !startTimestamp || !endTimestamp) return;

    if (userSelectedRangeType === RangeType.RECORDER_MODE) {
      if (!recorderWindowStart) return;
      if (prevTrainIdForDefects.current !== null && trainId !== prevTrainIdForDefects.current) {
        prevTrainIdForDefects.current = trainId;
        return;
      }
    }

    void getTrainDefects(undefined, { trainId, startTimestamp, endTimestamp }).then(json => {
      if (Array.isArray(json) && json.length) {
        const firstRate = normalizeDefectRow(json[0]).rate;
        const maxRate = json.reduce<number>((max, defect) => {
          const r = normalizeDefectRow(defect).rate;
          return r > max ? r : max;
        }, firstRate);
        setDefectsMaxRate(maxRate);

        const map: Record<string, number> = {};

        json.forEach(defect => {
          const n = normalizeDefectRow(defect);
          const { rate: Rate } = n;

          if (n.idPoint != null) {
            const keyAnyPoint = "3:any";
            map[keyAnyPoint] = map[keyAnyPoint] != null ? Math.max(map[keyAnyPoint], Rate) : Rate;
          }

          const mechForIndicator = n.resolvedIdTrainMechanism ?? n.idTrainMechanism;
          if (mechForIndicator != null) {
            const keyAnyMechanism = "16:any";
            map[keyAnyMechanism] = map[keyAnyMechanism] != null ? Math.max(map[keyAnyMechanism], Rate) : Rate;
          }

          if (n.idTrainComponent != null) {
            const keyAnyComponent = "18:any";
            map[keyAnyComponent] = map[keyAnyComponent] != null ? Math.max(map[keyAnyComponent], Rate) : Rate;
          }
        });

        setBindingDefectsMaxRates(map);
      } else {
        setDefectsMaxRate(null);
        setBindingDefectsMaxRates({});
      }
    });
  }, [trainId, startTimestamp, endTimestamp]);

  if (!shouldShowComponent) {
    return null;
  }

  const content = (
    <StyledWrapper
      sx={{ overflow: fitScheme || imageExpanded || (trainImage === TrainImage && showControls) ? "hidden" : "auto" }}
    >
      <Box
        sx={{
          flexBasis: imageExpanded ? "100%" : showControls ? "100%" : "30%",
          position: "relative",
          height: "100%",
          minHeight: 0,
          minWidth: fitScheme ? 0 : undefined,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {!imageExpanded && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              my: 0.5,
              flexShrink: fitScheme ? 0 : undefined,
            }}
          >
            <Box sx={{ flexShrink: 0, mr: 2 }}>
              <ToggleButtonGroup
                value={view}
                exclusive
                onChange={handleToggleView}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    padding: "2px",
                  },
                }}
                aria-label="Train Scheme View"
              >
                <ToggleButton value={TrainSchemeView.controls} key={TrainSchemeView.controls}>
                  <Tooltip
                    title={formatMessage({ id: "pages.scheme.trainScheme.view.controlsView" })}
                    arrow
                    disableInteractive
                  >
                    <GridView />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={TrainSchemeView.infoTable} key={TrainSchemeView.infoTable}>
                  <Tooltip
                    title={formatMessage({ id: "pages.scheme.trainScheme.view.tableView" })}
                    arrow
                    disableInteractive
                  >
                    <InfoOutlined />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        )}

        <ProgressOverlay
          isLoading={isPictureLoading || isControlsLoading}
          wrapperSx={{
            height: fitScheme ? 0 : imageExpanded ? "100%" : "auto",
            flex: fitScheme ? "1 1 0%" : undefined,
            minHeight: fitScheme ? 0 : undefined,
            minWidth: fitScheme ? 0 : undefined,
            overflow: fitScheme ? "hidden" : undefined,
          }}
        >
          <SchemeCanvas fit={fitScheme} width={imageWidth} height={imageHeight}>
            <StyledImage
              component="img"
              src={trainImage}
              ref={imageRef}
              sx={{
                width: imageExpanded
                  ? "100%"
                  : showControls
                    ? trainImage === TrainImage
                      ? "100%"
                      : imageWidth
                        ? `${imageWidth}px`
                        : "100%"
                    : "100%",
                maxWidth: imageExpanded
                  ? "100%"
                  : showControls
                    ? trainImage === TrainImage
                      ? "100%"
                      : imageWidth
                        ? `${imageWidth}px`
                        : "100%"
                    : "100%",
                cursor: imageNotFound ? "default" : imageExpanded ? "zoom-out" : "zoom-in",
                pt: imageExpanded ? "4px" : "0px",
                display: "block",
                ...(fitScheme
                  ? {
                      width: imageWidth,
                      height: imageHeight,
                      maxWidth: "none",
                      maxHeight: "none",
                      minWidth: 0,
                      minHeight: 0,
                      pt: 0,
                    }
                  : {}),
              }}
              onClick={toggleImageExpanded}
              onLoad={handleImageLoad}
            />
            {!imageExpanded && showControls && !isPictureLoading && trainImage !== TrainImage && (
              <>
                {formattedTrainControls.map(ctrl => {
                  const p: any = ctrl.props || {};
                  const left = Number(p.Left || 0);
                  const top = Number(p.Top || 0);
                  const width = p.Width ? `${p.Width}px` : "auto";
                  const height = p.Height ? `${p.Height}px` : "auto";

                  const resolvedBindId =
                    coerceSchemeBindId(ctrl.idBindObject) ??
                    coerceSchemeBindId(p.IdBindObject) ??
                    coerceSchemeBindId(p.idBindObject);
                  const jumpBindType =
                    coerceSchemeBindId(p.BindObjectType) ??
                    coerceSchemeBindId(p.bindObjectType) ??
                    coerceSchemeBindId(ctrl.bindObjectType);

                  const isDefectsControl = isBindingDefectsControl(ctrl);
                  const hasBindObject = resolvedBindId != null;
                  const isMainDefectsCtrl = ctrl.componentName === "TiLedRound" && jumpBindType === 2;
                  const canOpenDefectsDialog = isDefectsControl && !!trainId && hasBindObject;
                  const canOpenInfoDialog = !isDefectsControl && hasBindObject;
                  const canSchemeJump =
                    (ctrl.componentName === "TiSchemeButton" || ctrl.componentName === "TiSchemeImage") &&
                    hasBindObject &&
                    (jumpBindType === 1 || jumpBindType === 2);
                  const schemeOverlayClickable = canOpenDefectsDialog || canOpenInfoDialog || canSchemeJump;

                  let bindingKey: string | null = null;

                  if (ctrl.componentName === "TiLedRound") {
                    if (jumpBindType === 3) {
                      // второй круг: все дефекты, у которых idPoint не NULL
                      bindingKey = "3:any";
                    } else if (jumpBindType === 16) {
                      // третий круг: все дефекты, у которых idTrainMechanism не NULL
                      bindingKey = "16:any";
                    } else if (jumpBindType === 18) {
                      // четвертый круг: все дефекты, у которых idTrainComponent не NULL
                      bindingKey = "18:any";
                    }
                  }
                  const bindingRate = bindingKey ? bindingDefectsMaxRates[bindingKey] : undefined;

                  return (
                    <Box key={ctrl.idSchemeObject}>
                      {ctrl.componentName === "TiAnalogDisplay" && (
                        <TiAnalogDisplay
                          control={ctrl}
                          flashUnacknowledgedAlarms={flashUnacknowledgedAlarms}
                          flashFaultAlarms={flashFaultAlarms}
                        />
                      )}
                      {ctrl.componentName === "TiLabel" && (
                        <TiLabel
                          control={ctrl}
                          flashUnacknowledgedAlarms={flashUnacknowledgedAlarms}
                          flashFaultAlarms={flashFaultAlarms}
                        />
                      )}
                      {ctrl.componentName === "TiLedRound" && (
                        <TiLedRound
                          control={ctrl}
                          overrideAlarmStatus={
                            isMainDefectsCtrl
                              ? defectsMaxRate ?? undefined
                              : bindingRate !== undefined
                                ? bindingRate
                                : undefined
                          }
                        />
                      )}
                      {ctrl.componentName === "TiSevenSegmentAnalog" && (
                        <TiSevenSegmentAnalog
                          control={ctrl}
                          flashUnacknowledgedAlarms={flashUnacknowledgedAlarms}
                          flashFaultAlarms={flashFaultAlarms}
                        />
                      )}
                      {ctrl.componentName === "TiThermometer" && (
                        <TiThermometer
                          control={ctrl}
                          flashUnacknowledgedAlarms={flashUnacknowledgedAlarms}
                          flashFaultAlarms={flashFaultAlarms}
                        />
                      )}
                      {ctrl.componentName === "TiAngularGauge" && (
                        <TiAngularGauge
                          control={ctrl}
                          flashUnacknowledgedAlarms={flashUnacknowledgedAlarms}
                          flashFaultAlarms={flashFaultAlarms}
                        />
                      )}
                      {ctrl.componentName === "TiLinearGauge" && (
                        <TiLinearGauge
                          control={ctrl}
                          flashUnacknowledgedAlarms={flashUnacknowledgedAlarms}
                          flashFaultAlarms={flashFaultAlarms}
                        />
                      )}
                      {ctrl.componentName === "TiSchemeButton" && (
                        <TiSchemeButton
                          control={ctrl}
                          flashUnacknowledgedAlarms={flashUnacknowledgedAlarms}
                          flashFaultAlarms={flashFaultAlarms}
                        />
                      )}
                      {ctrl.componentName === "TiSchemeLine" && (
                        <TiSchemeLine
                          control={ctrl}
                          flashUnacknowledgedAlarms={flashUnacknowledgedAlarms}
                          flashFaultAlarms={flashFaultAlarms}
                        />
                      )}
                      {ctrl.componentName === "TiSchemeImage" && (
                        <TiSchemeImage
                          control={ctrl}
                          flashUnacknowledgedAlarms={flashUnacknowledgedAlarms}
                          flashFaultAlarms={flashFaultAlarms}
                        />
                      )}
                      {ctrl.componentName === "TiUnsupported" && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: ctrl.props.Left,
                            top: ctrl.props.Top,
                            width: ctrl.props.Width,
                            height: ctrl.props.Height,
                            border: "1px dashed rgba(0,0,0,0.35)",
                            backgroundColor: "transparent",
                            pointerEvents: "none",
                            zIndex: 49,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography fontSize="10px" sx={{ color: "rgba(0,0,0,0.65)", fontWeight: 700 }}>
                            {((ctrl as any).objectClass as string | undefined) ?? ctrl.componentName}
                          </Typography>
                        </Box>
                      )}

                      <Box
                        data-scheme-object-id={ctrl.idSchemeObject}
                        onClick={(e: MouseEvent<HTMLDivElement>) => {
                          handleControlClick(e, ctrl, resolvedBindId, jumpBindType, hasBindObject, isDefectsControl);
                        }}
                        onDoubleClick={() => {
                          handleControlDoubleClick(ctrl);
                        }}
                        sx={{
                          position: "absolute",
                          left: `${left}px`,
                          top: `${top}px`,
                          width,
                          height,
                          cursor: schemeOverlayClickable ? "pointer" : "default",
                          backgroundColor: "transparent",
                          zIndex: 50,
                        }}
                      />
                    </Box>
                  );
                })}
              </>
            )}
          </SchemeCanvas>
        </ProgressOverlay>
        <Dialog fullWidth maxWidth="sm" open={schemeObjectDialogOpen} onClose={handleDialogClose}>
          <DialogTitle id="scheme-object-info-dialog-title" sx={{ fontWeight: 700 }}>
            <FormattedMessage id="pages.scheme.trainScheme.schemeObjectInfo.title" />
          </DialogTitle>
          <DialogContent id="scheme-object-info-dialog-content">
            {schemeObjectInfo ? (
              (() => {
                const renderKeyValueTable = (rows: Array<[string, any]>) => (
                  <Table size="small" sx={{ mb: 1 }}>
                    <TableBody>
                      {rows.map(([k, v], rowIdx) => (
                        <TableRow key={`${k}-${rowIdx}`}>
                          <TableCell sx={{ fontWeight: 700, width: "40%" }}>{k}</TableCell>
                          <TableCell>{v == null || v === "" ? "—" : v}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                );

                const formatPickupSensor = (info: ISchemeObjectInfoResponse): string | null => {
                  if (info.pPickupType == null) {
                    return null;
                  }
                  return formatMessage(
                    { id: `enums.pickupType.${info.pPickupType}` },
                    { defaultMessage: String(info.pPickupType) },
                  );
                };

                const formatMeasureNumericDisplay = (value: number | null | undefined): string | number | null => {
                  if (value == null || typeof value !== "number" || !Number.isFinite(value)) {
                    return value ?? null;
                  }
                  return new Intl.NumberFormat("en-US", {
                    maximumFractionDigits: 6,
                    minimumFractionDigits: 0,
                    useGrouping: false,
                  }).format(value);
                };

                const formatPickupAngleDisplay = (angle: number | null) => {
                  const formatted = formatMeasureNumericDisplay(angle);
                  return formatted != null && formatted !== "" ? `${formatted}°` : null;
                };

                const buildPointSectionRows = (info: ISchemeObjectInfoResponse): Array<[string, any]> => {
                  const sensor = formatPickupSensor(info);
                  const angle = formatPickupAngleDisplay(info.pPickupAngle);
                  const rows: Array<[string, any]> = [
                    [formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.pName" }), info.pName],
                    [
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.pDescription" }),
                      info.pDescription,
                    ],
                  ];
                  if (sensor != null) {
                    rows.push([
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.pSensor" }),
                      sensor,
                    ]);
                  }
                  if (angle != null) {
                    rows.push([
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.pPickupAngle" }),
                      angle,
                    ]);
                  }
                  rows.push(
                    [
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.pDirection" }),
                      info.pDirection != null ? formatMessage({ id: `enums.pointDirection.${info.pDirection}` }) : "—",
                    ],
                    [
                      formatMessage({
                        id: "pages.scheme.trainScheme.schemeObjectInfo.headers.pControlledParameter",
                      }),
                      formatMessage({
                        id: `pages.journals.popup.train.points.${info.pControlledParameter}`,
                      }),
                    ],
                  );
                  return rows;
                };

                const isMeasurement = !!schemeObjectInfo.mName;
                const isPoint = !isMeasurement && !!schemeObjectInfo.pName;
                const hasTName = !!schemeObjectInfo.tName;
                const isTrain = !isMeasurement && !isPoint && hasTName;

                if (isMeasurement) {
                  const measurementRows: Array<[string, any]> = [
                    [
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.mName" }),
                      schemeObjectInfo.mName,
                    ],
                    [
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.mDescription" }),
                      schemeObjectInfo.mDescription,
                    ],
                    [
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.mType" }),
                      formatMessage({ id: `enums.measurementType.${schemeObjectInfo.mType}` }),
                    ],
                    [
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.mRangeType" }),
                      formatMessage({ id: `enums.measurementRangeType.${schemeObjectInfo.mRangeType}` }),
                    ],
                    [
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.mUnits" }),
                      formatMessage({ id: `enums.units.${schemeObjectInfo.mUnits}` }),
                    ],
                    [
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.mPresentation" }),
                      formatMessage({ id: `enums.unitsPresentation.${schemeObjectInfo.mPresentation}` }),
                    ],
                    [
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.mMinValidLevel" }),
                      formatMeasureNumericDisplay(schemeObjectInfo.mMinValidLevel),
                    ],
                    [
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.mMaxValidLevel" }),
                      formatMeasureNumericDisplay(schemeObjectInfo.mMaxValidLevel),
                    ],
                  ];
                  if (schemeObjectInfo.mAlarmLevel2 != null) {
                    measurementRows.push([
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.mAttention" }),
                      formatMeasureNumericDisplay(schemeObjectInfo.mAlarmLevel2),
                    ]);
                  }
                  if (schemeObjectInfo.mAlarmLevel3 != null) {
                    measurementRows.push([
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.mWarning" }),
                      formatMeasureNumericDisplay(schemeObjectInfo.mAlarmLevel3),
                    ]);
                  }
                  if (schemeObjectInfo.mAlarmLevel4 != null) {
                    measurementRows.push([
                      formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.mEmergency" }),
                      formatMeasureNumericDisplay(schemeObjectInfo.mAlarmLevel4),
                    ]);
                  }

                  return (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        {formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.measurement" })}
                      </Typography>
                      {renderKeyValueTable(measurementRows)}

                      {!!schemeObjectInfo.pName && (
                        <>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                            {formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.point" })}
                          </Typography>
                          {renderKeyValueTable(buildPointSectionRows(schemeObjectInfo))}
                        </>
                      )}
                    </Box>
                  );
                }

                if (isPoint) {
                  return (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        {formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.point" })}
                      </Typography>
                      {renderKeyValueTable(buildPointSectionRows(schemeObjectInfo))}
                    </Box>
                  );
                }

                if (isTrain) {
                  return (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        {formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.headers.train" })}
                      </Typography>

                      <Box sx={{ mt: 1 }}>
                        <InformationTable showTrain={false} isOnlyPage={onlyPage} />
                      </Box>
                    </Box>
                  );
                }
                return (
                  <Typography variant="subtitle1">
                    {formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.infoNotFound" })}
                  </Typography>
                );
              })()
            ) : (
              <Typography variant="subtitle1">
                {formatMessage({ id: "pages.scheme.trainScheme.schemeObjectInfo.infoNotFound" })}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <StyledButton onClick={handleDialogClose} sx={{ minWidth: 80, mb: 1, mr: 2 }}>
              <FormattedMessage id="form.buttons.ok" />
            </StyledButton>
          </DialogActions>
        </Dialog>
      </Box>
      <Box
        sx={{
          flexBasis: showControls ? "0%" : "70%",
          display: imageExpanded ? "none" : showControls ? "none" : "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box sx={{ display: showControls ? "none" : "block", height: "100%", minHeight: 0 }}>
          <InformationTable showTrain={false} isOnlyPage={onlyPage} />
        </Box>
      </Box>
      <TrainDefectsDialog
        open={defectsDialogOpen}
        trainId={trainId}
        binding={defectsDialogBinding}
        onClose={handleDefectsDialogClose}
        onMaxRateChange={setDefectsMaxRate}
      />
    </StyledWrapper>
  );

  return onlyPage && !!currentComponent ? (
    <OpenableWindow title={title} componentName="train">
      {content}
    </OpenableWindow>
  ) : (
    <DashboardAccordion
      expanded={train}
      title={title}
      setExpanded={setExpanded}
      componentName="train"
      isNarrow={isNarrow}
    >
      {content}
    </DashboardAccordion>
  );
};
